import { useRef, useState } from "react";
import { FileArrowUp, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { parseCsv, pdfToText, parseStatementText, SAMPLE_CSV } from "@/lib/statementParser";
import { usd, num } from "@/lib/format";

export function StatementImport({ onImported }) {
  const input = useRef(null);
  const [kind, setKind] = useState("balance");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      const snaps = isPdf ? parseStatementText(await pdfToText(file)) : parseCsv(await file.text(), kind);
      if (!snaps.length) toast.error("Couldn't find fund balances in that file. Check the format or try the sample CSV.");
      setPreview({ source: isPdf ? "pdf" : "csv", snapshots: snaps, name: file.name });
    } catch (e) {
      toast.error(`Could not read file: ${e.message}`);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tsp/import", { source: preview.source, snapshots: preview.snapshots, replace_holdings: true });
      toast.success(`Imported ${r.data.imported} statement snapshot(s)`);
      setPreview(null);
      onImported?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const sample = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: "text/csv" }));
    a.download = "tsp-sample-export.csv";
    a.click();
  };
  return (
    <div className="space-y-4" data-testid="statement-import">
      <div onClick={() => input.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files[0]); }}
        className="cursor-pointer rounded-md border-2 border-dashed border-slate-300 dark:border-slate-700 p-8 text-center hover:border-cyan-500 transition-colors" data-testid="statement-dropzone">
        <FileArrowUp size={28} className="mx-auto text-cyan-600" />
        <p className="mt-2 text-sm font-medium">{busy ? "Reading statement..." : "Drop a TSP PDF statement or CSV export"}</p>
        <p className="text-xs text-muted-foreground mt-1">Parsed in your browser. Only extracted numbers are sent.</p>
        <input ref={input} type="file" accept=".pdf,.csv" className="hidden" onChange={(e) => handle(e.target.files[0])} data-testid="statement-file-input" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RadioGroup value={kind} onValueChange={setKind} className="flex gap-4" data-testid="csv-value-kind">
          <div className="flex items-center gap-2"><RadioGroupItem value="balance" id="vk-b" /><Label htmlFor="vk-b" className="text-xs">CSV values are $ balances</Label></div>
          <div className="flex items-center gap-2"><RadioGroupItem value="shares" id="vk-s" /><Label htmlFor="vk-s" className="text-xs">CSV values are shares</Label></div>
        </RadioGroup>
        <Button variant="ghost" size="sm" onClick={sample} className="gap-1 text-xs" data-testid="download-sample-csv"><DownloadSimple size={14} /> Sample CSV</Button>
      </div>
      {preview?.snapshots?.length > 0 && (
        <div className="rounded-md border border-border p-4 space-y-3" data-testid="import-preview">
          <p className="text-sm font-medium">{preview.name}: {preview.snapshots.length} snapshot(s) found</p>
          <div className="max-h-48 overflow-auto text-xs font-mono-num space-y-2">
            {preview.snapshots.map((s) => (
              <div key={s.date}>
                <p className="text-muted-foreground">{s.date}{s.loan_balance != null ? ` · Loan ${usd(s.loan_balance)}` : ""}{s.agency_match != null ? ` · Agency ${usd(s.agency_match)}` : ""}</p>
                <p>{Object.entries(Object.keys(s.balances).length ? s.balances : s.holdings).map(([f, v]) => `${f}: ${Object.keys(s.balances).length ? usd(v) : num(v)}`).join("  ·  ")}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={confirm} disabled={busy} data-testid="import-confirm-button">Import & replace TSP holdings</Button>
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="import-cancel-button">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
